<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="headline font-weight-bold">Mis Rutinas de Entrenamiento</span>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="openCreateRoutineDialog" class="mr-2">
                        <v-icon left>mdi-plus</v-icon>
                        Nueva Rutina
                    </v-btn>
                </v-card-title>

                <!-- Tabs -->
                <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
                    <v-tab>
                        <v-icon left>mdi-dumbbell</v-icon>
                        Mis Rutinas
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-book-open</v-icon>
                        Rutinas Compartidas
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab" class="transparent">
                    <!-- Tab 1: Mis Rutinas -->
                    <v-tab-item>
                        <v-row>
                            <v-col
                                v-for="routine in myRoutines"
                                :key="routine.id"
                                cols="12"
                                sm="6"
                                md="4">
                                <v-card class="elevation-2 h-100">
                                    <v-card-title class="pb-2">
                                        <div class="headline">{{ routine.name }}</div>
                                    </v-card-title>
                                    <v-card-subtitle class="pb-2">{{ routine.description }}</v-card-subtitle>

                                    <v-divider></v-divider>

                                    <v-card-text class="pa-4">
                                        <div class="mb-3">
                                            <div class="caption grey--text">Ejercicios</div>
                                            <div class="body-1 font-weight-bold">{{ routine.exercises.length }}</div>
                                        </div>
                                        <div class="mb-3">
                                            <div class="caption grey--text">Creada</div>
                                            <div class="body-2">{{ formatDate(routine.created_date) }}</div>
                                        </div>
                                    </v-card-text>

                                    <v-card-actions>
                                        <v-btn small text color="primary" @click="openEditRoutine(routine)">
                                            <v-icon small left>mdi-pencil</v-icon>
                                            Editar
                                        </v-btn>
                                        <v-btn small text color="info" @click="viewRoutineDetails(routine)">
                                            <v-icon small left>mdi-eye</v-icon>
                                            Ver
                                        </v-btn>
                                        <v-spacer></v-spacer>
                                        <v-btn small text color="error" @click="deleteRoutine(routine.id)">
                                            <v-icon small>mdi-delete</v-icon>
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-row v-if="myRoutines.length === 0" class="justify-center py-8">
                            <v-col cols="12" class="text-center">
                                <v-icon size="48" class="grey--text mb-2">mdi-dumbbell</v-icon>
                                <div class="subtitle-1 grey--text">Sin rutinas creadas</div>
                                <v-btn color="primary" text @click="openCreateRoutineDialog" class="mt-2">
                                    <v-icon left>mdi-plus</v-icon>
                                    Crear primera rutina
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <!-- Tab 2: Rutinas Compartidas -->
                    <v-tab-item>
                        <v-row>
                            <v-col
                                v-for="routine in sharedRoutines"
                                :key="routine.id"
                                cols="12"
                                sm="6"
                                md="4">
                                <v-card class="elevation-2 h-100">
                                    <v-card-title class="pb-2">
                                        <div class="headline">{{ routine.name }}</div>
                                    </v-card-title>
                                    <v-card-subtitle class="pb-2">{{ routine.description }}</v-card-subtitle>

                                    <v-divider></v-divider>

                                    <v-card-text class="pa-4">
                                        <div class="mb-3">
                                            <div class="caption grey--text">Coach</div>
                                            <div class="body-2 font-weight-bold">{{ getCoachName(routine.coach_id) }}</div>
                                        </div>
                                        <div class="mb-3">
                                            <div class="caption grey--text">Ejercicios</div>
                                            <div class="body-1 font-weight-bold">{{ routine.exercises.length }}</div>
                                        </div>
                                    </v-card-text>

                                    <v-card-actions>
                                        <v-btn small text color="info" @click="viewRoutineDetails(routine)">
                                            <v-icon small left>mdi-eye</v-icon>
                                            Ver
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                </v-tabs-items>
            </v-flex>
        </v-layout>

        <!-- Diálogo: Crear/Editar Rutina -->
        <v-dialog v-model="createRoutineDialog" max-width="800px" persistent>
            <v-card v-if="editingRoutine">
                <v-card-title>
                    <div>
                        <div class="headline">{{ isEditingRoutine ? 'Editar Rutina' : 'Nueva Rutina de Entrenamiento' }}</div>
                        <div class="caption grey--text">Configura los detalles de la rutina</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="createRoutineDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="routineForm" v-model="validRoutineForm" lazy-validation>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="editingRoutine.name"
                                    label="Nombre de la Rutina"
                                    outlined
                                    dense
                                    :rules="[v => v || 'Se requiere un nombre']"
                                    placeholder="Ej: Pecho y Tríceps"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="editingRoutine.description"
                                    label="Descripción"
                                    outlined
                                    dense
                                    rows="2"
                                    placeholder="Describe el objetivo de esta rutina"
                                    counter
                                    maxlength="200"
                                ></v-textarea>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4"></v-divider>

                        <div class="subtitle-2 font-weight-bold mb-4">
                            <v-icon small class="mr-2">mdi-dumbbell</v-icon>
                            Ejercicios ({{ editingRoutine.exercises.length }})
                            <v-btn small text color="primary" @click="openAddExerciseDialog" class="ml-2">
                                <v-icon small left>mdi-plus</v-icon>
                                Agregar Ejercicio
                            </v-btn>
                        </div>

                        <v-list dense v-if="editingRoutine.exercises.length > 0">
                            <v-list-item
                                v-for="exercise in editingRoutine.exercises"
                                :key="exercise.id"
                                class="mb-2">
                                <v-card class="w-100">
                                    <v-card-text class="pa-3">
                                        <v-row align="center" class="mb-2">
                                            <v-col cols="auto">
                                                <span class="body-2 font-weight-bold">{{ exercise.name }}</span>
                                            </v-col>
                                            <v-spacer></v-spacer>
                                            <v-col cols="auto">
                                                <v-btn
                                                    x-small
                                                    text
                                                    color="primary"
                                                    @click="openEditExerciseDialog(exercise)">
                                                    <v-icon x-small>mdi-pencil</v-icon>
                                                </v-btn>
                                                <v-btn
                                                    x-small
                                                    text
                                                    color="error"
                                                    @click="removeExercise(exercise.id)">
                                                    <v-icon x-small>mdi-delete</v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                        <div class="caption grey--text mb-2">{{ exercise.description }}</div>
                                        <v-row dense>
                                            <v-col cols="auto">
                                                <span class="caption"><strong>Series:</strong> {{ exercise.series }}</span>
                                            </v-col>
                                            <v-col cols="auto">
                                                <span class="caption"><strong>Reps:</strong> {{ exercise.reps }}</span>
                                            </v-col>
                                            <v-col cols="auto">
                                                <span class="caption"><strong>Peso:</strong> {{ exercise.weight }}</span>
                                            </v-col>
                                            <v-col cols="auto">
                                                <span class="caption"><strong>Descanso:</strong> {{ exercise.restTime }}</span>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-list-item>
                        </v-list>

                        <v-card v-else class="grey lighten-5 pa-4 text-center">
                            <v-icon class="grey--text mb-2" size="32">mdi-alert-circle</v-icon>
                            <div class="body-2 grey--text">Sin ejercicios agregados</div>
                        </v-card>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="createRoutineDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveRoutine" :disabled="!validRoutineForm">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar Rutina
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Agregar/Editar Ejercicio -->
        <v-dialog v-model="exerciseDialog" max-width="600px" persistent>
            <v-card v-if="editingExercise">
                <v-card-title>
                    <div>
                        <div class="headline">{{ isEditingExercise ? 'Editar Ejercicio' : 'Nuevo Ejercicio' }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="exerciseDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="exerciseForm" v-model="validExerciseForm" lazy-validation>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="editingExercise.name"
                                    label="Nombre del Ejercicio"
                                    outlined
                                    dense
                                    :rules="[v => v || 'Se requiere un nombre']"
                                    placeholder="Ej: Press de Banca"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="editingExercise.description"
                                    label="Descripción"
                                    outlined
                                    dense
                                    rows="2"
                                    placeholder="Detalles del ejercicio"
                                    counter
                                    maxlength="150"
                                ></v-textarea>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model.number="editingExercise.series"
                                    label="Series"
                                    type="number"
                                    outlined
                                    dense
                                    min="1"
                                    :rules="[v => v >= 1 || 'Mínimo 1 serie']"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model.number="editingExercise.reps"
                                    label="Repeticiones"
                                    type="number"
                                    outlined
                                    dense
                                    min="1"
                                    :rules="[v => v >= 1 || 'Mínimo 1 repetición']"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="editingExercise.weight"
                                    label="Peso / Resistencia"
                                    outlined
                                    dense
                                    placeholder="Ej: 80 kg, Sin peso, Peso corporal"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="editingExercise.restTime"
                                    label="Tiempo de Descanso"
                                    outlined
                                    dense
                                    placeholder="Ej: 2 min"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="editingExercise.notes"
                                    label="Notas Especiales"
                                    outlined
                                    dense
                                    rows="2"
                                    placeholder="Ej: Controlar la bajada, evitar rebote"
                                    counter
                                    maxlength="150"
                                ></v-textarea>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="exerciseDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveExercise" :disabled="!validExerciseForm">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar Ejercicio
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Ver Detalles de Rutina -->
        <v-dialog v-model="detailsDialog" max-width="800px">
            <v-card v-if="viewingRoutine">
                <v-card-title>
                    <div>
                        <div class="headline">{{ viewingRoutine.name }}</div>
                        <div class="caption grey--text">por {{ getCoachName(viewingRoutine.coach_id) }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="detailsDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <div class="mb-4">
                        <div class="subtitle-2 font-weight-bold mb-2">Descripción</div>
                        <div class="body-2">{{ viewingRoutine.description }}</div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="subtitle-2 font-weight-bold mb-3">Ejercicios ({{ viewingRoutine.exercises.length }})</div>

                    <v-list v-if="viewingRoutine.exercises.length > 0" dense>
                        <v-list-item
                            v-for="(exercise, idx) in viewingRoutine.exercises"
                            :key="exercise.id"
                            class="mb-3">
                            <v-card class="w-100 grey lighten-4">
                                <v-card-text class="pa-3">
                                    <div class="d-flex align-center mb-2">
                                        <span class="body-2 font-weight-bold">{{ idx + 1 }}. {{ exercise.name }}</span>
                                    </div>
                                    <div class="caption grey--text mb-2">{{ exercise.description }}</div>

                                    <v-row dense class="mb-2">
                                        <v-col cols="auto">
                                            <span class="caption"><strong>Series:</strong> {{ exercise.series }}</span>
                                        </v-col>
                                        <v-col cols="auto">
                                            <span class="caption"><strong>Reps:</strong> {{ exercise.reps }}</span>
                                        </v-col>
                                        <v-col cols="auto">
                                            <span class="caption"><strong>Peso:</strong> {{ exercise.weight }}</span>
                                        </v-col>
                                        <v-col cols="auto">
                                            <span class="caption"><strong>Descanso:</strong> {{ exercise.restTime }}</span>
                                        </v-col>
                                    </v-row>

                                    <div v-if="exercise.notes" class="caption grey--text">
                                        <strong>Notas:</strong> {{ exercise.notes }}
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
import gymService from '@/services/gym.js'

export default {
  name: 'CoachRoutines',
  data: () => ({
    activeTab: 0,
    createRoutineDialog: false,
    exerciseDialog: false,
    detailsDialog: false,
    validRoutineForm: false,
    validExerciseForm: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    currentCoachId: 1, // Mock: en un caso real vendría del auth
    editingRoutine: null,
    editingExercise: null,
    viewingRoutine: null,
    isEditingRoutine: false,
    isEditingExercise: false
  }),
  computed: {
    myRoutines () {
      return gymService.routines.filter(r => r.coach_id === this.currentCoachId)
    },
    sharedRoutines () {
      return gymService.routines.filter(r => r.coach_id !== this.currentCoachId)
    }
  },
  methods: {
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getCoachName (coachId) {
      const coach = gymService.getCoachById(coachId)
      return coach ? coach.name : 'Desconocido'
    },
    openCreateRoutineDialog () {
      this.isEditingRoutine = false
      this.editingRoutine = {
        name: '',
        description: '',
        coach_id: this.currentCoachId,
        exercises: []
      }
      this.createRoutineDialog = true
    },
    openEditRoutine (routine) {
      this.isEditingRoutine = true
      this.editingRoutine = JSON.parse(JSON.stringify(routine))
      this.createRoutineDialog = true
    },
    saveRoutine () {
      if (this.$refs.routineForm.validate()) {
        if (this.isEditingRoutine) {
          const index = gymService.routines.findIndex(r => r.id === this.editingRoutine.id)
          if (index > -1) {
            gymService.routines[index] = this.editingRoutine
            this.snackbarMessage = 'Rutina actualizada correctamente'
          }
        } else {
          gymService.createRoutine(this.editingRoutine.name, this.editingRoutine.description, this.currentCoachId)
          const newRoutine = gymService.routines[gymService.routines.length - 1]
          newRoutine.exercises = this.editingRoutine.exercises
          this.snackbarMessage = 'Rutina creada correctamente'
        }
        this.snackbarColor = 'success'
        this.snackbar = true
        this.createRoutineDialog = false
      }
    },
    openAddExerciseDialog () {
      this.isEditingExercise = false
      this.editingExercise = {
        name: '',
        description: '',
        series: 3,
        reps: 10,
        weight: '',
        restTime: '1 min',
        notes: ''
      }
      this.exerciseDialog = true
    },
    openEditExerciseDialog (exercise) {
      this.isEditingExercise = true
      this.editingExercise = JSON.parse(JSON.stringify(exercise))
      this.exerciseDialog = true
    },
    saveExercise () {
      if (this.$refs.exerciseForm.validate()) {
        if (this.isEditingExercise) {
          const index = this.editingRoutine.exercises.findIndex(e => e.id === this.editingExercise.id)
          if (index > -1) {
            this.editingRoutine.exercises[index] = this.editingExercise
          }
        } else {
          this.editingExercise.id = Math.max(...this.editingRoutine.exercises.map(e => e.id), 0) + 1
          this.editingRoutine.exercises.push(JSON.parse(JSON.stringify(this.editingExercise)))
        }
        this.exerciseDialog = false
      }
    },
    removeExercise (exerciseId) {
      this.editingRoutine.exercises = this.editingRoutine.exercises.filter(e => e.id !== exerciseId)
      this.snackbarMessage = 'Ejercicio eliminado'
      this.snackbarColor = 'info'
      this.snackbar = true
    },
    viewRoutineDetails (routine) {
      this.viewingRoutine = JSON.parse(JSON.stringify(routine))
      this.detailsDialog = true
    },
    deleteRoutine (routineId) {
      if (confirm('¿Eliminar esta rutina?')) {
        const index = gymService.routines.findIndex(r => r.id === routineId)
        if (index > -1) {
          gymService.routines.splice(index, 1)
          this.snackbarMessage = 'Rutina eliminada'
          this.snackbarColor = 'info'
          this.snackbar = true
        }
      }
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.w-100 {
  width: 100%;
}

.h-100 {
  height: 100%;
}
</style>
